import { useCallback } from 'react';
import { subMinutes, addMinutes } from 'date-fns';
import { SchedulerPro, EventModel, ResourceModel } from '@bryntum/schedulerpro';
import { mapDate } from '../helpers/mapDate';

/**
 * Check if two time intervals overlap
 * Uses half-open interval logic: [start, end)
 * Two intervals overlap if: A.start < B.end AND A.end > B.start
 *
 * This is the standard interval overlap algorithm that correctly handles:
 * - Adjacent events (ending at 14:00, starting at 14:00) - NO overlap
 * - Partial overlaps
 * - Complete containment
 */
function intervalsOverlap(
  aStart: Date,
  aEnd: Date,
  bStart: Date,
  bEnd: Date
): boolean {
  return aStart < bEnd && aEnd > bStart;
}

interface EventDragData {
  startDate: Date | string;
  endDate: Date | string;
  eventRecord: {
    data?: {
      id?: string;
      preamble?: { _magnitude?: number } | string;
      postamble?: { _magnitude?: number } | string;
    };
    originalData?: {
      id?: string;
      resourceId?: string;
      taskReferenceId?: string;
      mustStartAfter?: string | null;
      mustEndBefore?: string | null;
    };
  };
  resourceRecord: {
    originalData?: {
      id?: string;
    };
  };
  newResource?: {
    originalData?: {
      id?: string;
    };
  };
}

interface ValidationResult {
  valid: boolean;
  message?: string;
}

/**
 * Minimal shape of the Bryntum drop context we interact with when rejecting a
 * drop. Narrow by design so the helper below is trivially testable.
 */
export interface DropRejectableContext {
  valid?: boolean;
  finalize: (success: boolean) => void;
}

/**
 * Apply a validation result to a Bryntum drop context. Returns `true` if the
 * drop should proceed. On rejection, BOTH `context.valid = false` AND
 * `context.finalize(false)` are required — Bryntum otherwise leaves the drag
 * proxy in a half-committed state when the sync validator rejects inside
 * `beforeEventDropFinalize`.
 */
export function applyDropValidationResult(
  context: DropRejectableContext,
  result: ValidationResult
): boolean {
  if (result.valid) return true;
  context.valid = false;
  context.finalize(false);
  return false;
}

/**
 * Event record returned from Bryntum's getEvents query
 */
interface EventInRange {
  originalData?: { resourceId?: string; id?: string };
  startDate?: Date;
  endDate?: Date;
  data?: { name?: string };
}

/**
 * Parse preamble/postamble value to minutes
 * Handles both object format ({_magnitude: number}) and string format ('30min')
 */
function parseDurationMinutes(value: unknown): number {
  if (!value) return 0;

  // Object format from Bryntum
  if (typeof value === 'object' && value !== null && '_magnitude' in value) {
    return (value as { _magnitude: number })._magnitude || 0;
  }

  // String format like '30min'
  if (typeof value === 'string') {
    const match = value.match(/(\d+)min/);
    return match ? parseInt(match[1], 10) : 0;
  }

  return 0;
}

// Shared across both bounds of the constraint-window check and mirrored by the
// form-path validators in TaskDetails.tsx and ComplexLossDetails.tsx — keep
// the wording in sync if you touch it.
const START_OUT_OF_WINDOW_MESSAGE =
  'Cannot move task start time from the initially scheduled window.';

/**
 * Validate that the event's START date falls within its appointment window
 * `[mustStartAfter, mustEndBefore]`. Matches legacy scheduler v1 behavior: the
 * start must remain inside the window on both ends, while the end date itself
 * is allowed to extend past `mustEndBefore` (PR 37016).
 * Returns valid:true if no constraints exist (non-task events or missing data).
 */
function validateConstraintWindow(data: EventDragData): ValidationResult {
  const original = data.eventRecord.originalData;

  // Only tasks have constraints — skip for blocked time events
  const isTask = original?.taskReferenceId && original.taskReferenceId !== '';
  if (!isTask) {
    return { valid: true };
  }

  const startDate = mapDate(data.startDate);

  if (original?.mustStartAfter) {
    const mustStartAfter = mapDate(original.mustStartAfter);
    if (startDate < mustStartAfter) {
      return { valid: false, message: START_OUT_OF_WINDOW_MESSAGE };
    }
  }

  if (original?.mustEndBefore) {
    const mustEndBefore = new Date(original.mustEndBefore);
    if (startDate > mustEndBefore) {
      return { valid: false, message: START_OUT_OF_WINDOW_MESSAGE };
    }
  }

  return { valid: true };
}

/**
 * Validate that non-task events (blocked time) cannot change resource.
 * Tasks can be reassigned; blocked time events stay on their original resource.
 */
function validateResourceChange(data: EventDragData): ValidationResult {
  const original = data.eventRecord.originalData;
  const isTask = original?.taskReferenceId && original.taskReferenceId !== '';

  // Tasks are allowed to change resource
  if (isTask) {
    return { valid: true };
  }

  const originalResourceId = original?.resourceId;
  const newResourceId =
    data.newResource?.originalData?.id || data.resourceRecord.originalData?.id;

  if (
    originalResourceId &&
    newResourceId &&
    originalResourceId !== newResourceId
  ) {
    return {
      valid: false,
      message: 'Blocked time events cannot be moved to a different resource',
    };
  }

  return { valid: true };
}

/**
 * Hook for detecting scheduling conflicts
 *
 * Used to validate event drag/drop and resize operations,
 * checking for overlaps with existing events while accounting
 * for drive time (preamble/postamble), constraint windows,
 * and resource-change rules.
 */
export function useConflictDetection(
  schedulerRef: React.RefObject<{ instance?: SchedulerPro }>
) {
  /**
   * V1 parity: block drops whose time range falls on non-working time for the
   * target resource. V1 used Bryntum's `isDateRangeAvailable`, which checks
   * both the resource's calendar and existing events. Overlap is handled by
   * `validateEventPlacement` (with drive-time buffering), so this validator
   * runs afterward — any false result here is a calendar violation.
   */
  const validateWorkingCalendar = useCallback(
    (data: EventDragData): ValidationResult => {
      const scheduler = schedulerRef.current?.instance;
      if (!scheduler?.isDateRangeAvailable) {
        return { valid: true };
      }

      const targetResourceId =
        data.newResource?.originalData?.id ||
        data.resourceRecord.originalData?.id;
      if (!targetResourceId) {
        return { valid: true };
      }

      const resource = scheduler.resourceStore?.getById?.(targetResourceId) as
        | ResourceModel
        | null
        | undefined;
      if (!resource) {
        return { valid: true };
      }

      const available = scheduler.isDateRangeAvailable(
        mapDate(data.startDate),
        mapDate(data.endDate),
        data.eventRecord as unknown as EventModel | null,
        resource
      );

      if (available === false) {
        return {
          valid: false,
          message: 'Cannot be scheduled on non-working time',
        };
      }

      return { valid: true };
    },
    [schedulerRef]
  );

  /**
   * Validate if an event can be placed at the target position
   * without conflicting with other events on the same resource
   */
  const validateEventPlacement = useCallback(
    (data: EventDragData): ValidationResult => {
      const scheduler = schedulerRef.current?.instance;
      if (!scheduler) {
        return { valid: true };
      }

      const { eventRecord, resourceRecord } = data;
      const draggedEvent = eventRecord.data;
      const resourceId = resourceRecord.originalData?.id;

      // If no resource ID, can't validate
      if (!resourceId) {
        return { valid: true };
      }

      // Get preamble and postamble durations (drive times)
      const preambleMinutes = parseDurationMinutes(draggedEvent?.preamble);
      const postambleMinutes = parseDurationMinutes(draggedEvent?.postamble);

      // Get events in the affected time range
      const effectiveStartDate = subMinutes(
        mapDate(data.startDate),
        preambleMinutes
      );
      const effectiveEndDate = addMinutes(
        mapDate(data.endDate),
        postambleMinutes
      );

      const eventsInRange = scheduler.eventStore?.getEvents?.({
        startDate: effectiveStartDate,
        endDate: effectiveEndDate,
      }) as EventInRange[] | undefined;

      // Filter to only events on the same resource (excluding the dragged event)
      const draggedEventId = draggedEvent?.id;
      const conflictingEvents = eventsInRange?.filter((event) => {
        return (
          event.originalData?.resourceId === resourceId &&
          event.originalData?.id !== draggedEventId
        );
      });

      if (!conflictingEvents?.length) {
        return { valid: true };
      }

      // Check ALL conflicting events for actual overlaps accounting for drive time
      // Uses standard interval overlap algorithm: two intervals [A_start, A_end) and [B_start, B_end)
      // overlap if A_start < B_end AND A_end > B_start
      for (const conflictEvent of conflictingEvents) {
        if (!conflictEvent.startDate || !conflictEvent.endDate) {
          continue;
        }

        const hasOverlap = intervalsOverlap(
          effectiveStartDate,
          effectiveEndDate,
          conflictEvent.startDate,
          conflictEvent.endDate
        );

        if (hasOverlap) {
          return {
            valid: false,
            message: conflictEvent.data?.name
              ? `Event overlaps with "${conflictEvent.data.name}"`
              : 'Event overlaps existing event for this resource',
          };
        }
      }

      return { valid: true };
    },
    [schedulerRef]
  );

  /**
   * Combined validator: constraint window + resource change + overlap detection.
   * Returns the first failing validation or valid:true if all pass.
   * Used as Bryntum's eventDrag validatorFn and beforeEventDropFinalize.
   */
  const createEventDragValidator = useCallback(
    () =>
      (data: EventDragData): ValidationResult => {
        // 1. Non-task events cannot change resource
        const resourceResult = validateResourceChange(data);
        if (!resourceResult.valid) return resourceResult;

        // 2. Check start stays inside appointment window [mustStartAfter, mustEndBefore]
        const constraintResult = validateConstraintWindow(data);
        if (!constraintResult.valid) return constraintResult;

        // 3. Check for overlaps (buffered by drive time)
        const placementResult = validateEventPlacement(data);
        if (!placementResult.valid) return placementResult;

        // 4. Check the resource's working calendar last so overlap messages
        //    win when both conditions would fail.
        return validateWorkingCalendar(data);
      },
    [validateEventPlacement, validateWorkingCalendar]
  );

  /**
   * Create a validator function for Bryntum's drag/resize finalize events
   * Both beforeEventDropFinalize and beforeEventResizeFinalize use the same validation
   */
  const createValidator = useCallback(
    () => (data: EventDragData) => validateEventPlacement(data),
    [validateEventPlacement]
  );

  return {
    validateEventPlacement,
    validateConstraintWindow,
    validateWorkingCalendar,
    createEventDragValidator,
    createValidator,
    // Backwards-compatible aliases
    createDragValidator: createEventDragValidator,
    createResizeValidator: createValidator,
  };
}
